import React, { useEffect, useRef, useState } from 'react';
import { commissionsService } from '../../services/commissionsService';

interface Props {
  commissionId: string;
  clientSecret: string;
  onClose: () => void;
  onSuccess?: () => void;
}

const CommissionPaymentModal: React.FC<Props> = ({ commissionId, clientSecret, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [commission, setCommission] = useState<any>(null);

  const stripeRef = useRef<any>(null);
  const elementsRef = useRef<any>(null);
  const paymentElementRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const loadCommission = async () => {
      try {
        const { commission } = await commissionsService.get(commissionId);
        setCommission(commission);
      } catch (e: any) {
        setError(e?.response?.data?.error || 'Failed to load commission details');
      }
    };
    loadCommission();
  }, [commissionId]);

  useEffect(() => {
    const initElements = async () => {
      if (!clientSecret || !paymentElementRef.current) return;
      const pk = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;
      if (!pk) {
        setError('Stripe is not configured. Missing VITE_STRIPE_PUBLISHABLE_KEY');
        return;
      }
      const { loadStripe } = await import('@stripe/stripe-js');
      const stripe = await loadStripe(pk);
      if (!stripe) {
        setError('Failed to load Stripe');
        return;
      }
      stripeRef.current = stripe;
      const elements = stripe.elements({ clientSecret, appearance: { theme: 'stripe' } });
      elementsRef.current = elements;
      const paymentElement = elements.create('payment');
      paymentElement.mount(paymentElementRef.current);
    };
    initElements();
  }, [clientSecret]);

  const confirmPayment = async () => {
    if (!stripeRef.current || !elementsRef.current || !clientSecret) return;
    setLoading(true);
    setError(null);
    try {
      const stripe = stripeRef.current;
      const elements = elementsRef.current;
      const result = await stripe.confirmPayment({ elements, clientSecret, redirect: 'if_required' });
      if (result.error) {
        setError(result.error.message || 'Payment failed');
        return;
      }
      setSuccess(true);
      onSuccess?.();
    } catch (e: any) {
      setError(e?.message || 'Payment confirmation failed');
    } finally {
      setLoading(false);
    }
  };

  const formattedAmount = commission ? new Intl.NumberFormat('en-US', { style: 'currency', currency: commission.currency || 'USD' }).format(
    (commission.amount && commission.amount.toNumber ? commission.amount.toNumber() : Number(commission.amount)) || 0
  ) : '';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Complete commission payment</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">✕</button>
        </div>

        {!success ? (
          <div className="space-y-4">
            {commission && (
              <div className="text-sm text-gray-700">
                <div className="font-medium">{commission.title}</div>
                <div className="flex items-center justify-between mt-1 bg-gray-50 rounded-md p-2">
                  <span>Amount</span>
                  <span className="font-semibold">{formattedAmount}</span>
                </div>
              </div>
            )}
            <div ref={paymentElementRef} />
            {error && <div className="text-sm text-red-600">{error}</div>}
            <button onClick={confirmPayment} disabled={loading} className="w-full bg-green-600 text-white rounded-md py-2 hover:bg-green-700 disabled:opacity-50">
              {loading ? 'Processing…' : 'Authorize Payment'}
            </button>
          </div>
        ) : (
          <div className="space-y-4 text-center">
            <div className="text-green-600 text-3xl">✓</div>
            <div className="text-lg font-medium">Payment authorized</div>
            <div className="text-sm text-gray-600">Funds are held in escrow. The creator will start your commission and you can approve the final delivery to release payment.</div>
            <button onClick={onClose} className="w-full bg-indigo-600 text-white rounded-md py-2 hover:bg-indigo-700">
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CommissionPaymentModal;
